/**
 * I18nInput Component
 * Tab-based multilingual input component
 * Requirements 2.1, 2.2, 2.3, 2.4, 2.5, 2.6 implementation
 */

import React, { useState, useCallback, useMemo, useEffect as _useEffect } from 'react'; // Task 8에서 사용 예정
import { Tabs, Input, Form as _Form, Badge, Typography } from 'antd'; // Task 8에서 사용 예정
import type { TabsProps } from 'antd';
import {
  SUPPORTED_LANGUAGES,
  LanguageCode,
  LANGUAGE_CODES as _LANGUAGE_CODES,
} from '../../constants/i18n/languages'; // Task 8에서 사용 예정
import { I18N_CONFIG, getFieldConfig, isRequiredLanguage } from '../../../config/i18nConfig';
import { I18nFieldData } from '../../types/i18n/i18nTypes';
import { I18nValidation } from '../../services/i18n/i18nValidation';
import './I18nInput.css';

const { TextArea } = Input;
const { Text } = Typography;

export interface I18nInputProps {
  /** Field label */
  label?: string;
  /** i18n object value */
  value?: I18nFieldData;
  /** Value change handler */
  onChange?: (value: I18nFieldData) => void;
  /** Whether to use multiline input */
  multiline?: boolean;
  /** Number of rows for textarea */
  rows?: number;
  /** Whether the field is required */
  required?: boolean;
  /** Helper text */
  helperText?: string;
  /** Error state */
  error?: boolean;
  /** Field name (for validation) */
  fieldName?: string;
  /** Whether to disable the field */
  disabled?: boolean;
  /** Per-language placeholders */
  placeholder?: Partial<Record<LanguageCode, string>>;
  /** Validation trigger */
  validateTrigger?: 'onChange' | 'onBlur' | 'onSubmit';
}

/**
 * I18nInput Component
 * Provides a tab-based multilingual input interface.
 */
export const I18nInput: React.FC<I18nInputProps> = ({
  label,
  value = {},
  onChange,
  multiline = false,
  rows = 3,
  required = false,
  helperText,
  error = false,
  fieldName = 'title',
  disabled = false,
  placeholder = {},
  validateTrigger = 'onChange',
}) => {
  // 현재 활성 탭 (언어)
  const [activeTab, setActiveTab] = useState<LanguageCode>('ko');

  // 언어별 검증 오류 상태
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<LanguageCode, string>>>({});

  // 지원 언어 목록
  const supportedLanguages = useMemo(() => {
    return I18N_CONFIG.supportedLanguages;
  }, []);

  // 번역 상태 계산 (각 언어별로 내용이 있는지 확인)
  const translationStatus = useMemo(() => {
    const status: Record<LanguageCode, boolean> = {} as Record<LanguageCode, boolean>;
    supportedLanguages.forEach(lang => {
      status[lang] = Boolean(value[lang]?.trim());
    });
    return status;
  }, [value, supportedLanguages]);

  // 필드 설정 가져오기
  const fieldConfig = useMemo(() => {
    return getFieldConfig(fieldName);
  }, [fieldName]);

  // 개별 언어 필드 값 변경 핸들러
  const handleFieldChange = useCallback(
    (language: LanguageCode, fieldValue: string) => {
      if (!onChange) return;

      const newValue: I18nFieldData = {
        ...value,
        [language]: fieldValue || undefined, // 빈 문자열은 undefined로 처리
      };

      // 실시간 검증 (onChange 트리거인 경우)
      if (validateTrigger === 'onChange') {
        const validation = I18nValidation.validateRealtime(fieldName as any, language, fieldValue);

        setFieldErrors(prev => ({
          ...prev,
          [language]: validation.isValid ? '' : validation.message || '',
        }));
      }

      onChange(newValue);
    },
    [value, onChange, fieldName, validateTrigger]
  );

  // 필드 블러 핸들러 (onBlur 검증)
  const handleFieldBlur = useCallback(
    (language: LanguageCode, fieldValue: string) => {
      if (validateTrigger === 'onBlur') {
        const validation = I18nValidation.validateRealtime(fieldName as any, language, fieldValue);

        setFieldErrors(prev => ({
          ...prev,
          [language]: validation.isValid ? '' : validation.message || '',
        }));
      }
    },
    [fieldName, validateTrigger]
  );

  // 탭 아이템 생성
  const tabItems: TabsProps['items'] = useMemo(() => {
    return supportedLanguages.map(language => {
      const languageInfo = SUPPORTED_LANGUAGES[language];
      const hasTranslation = translationStatus[language];
      const isRequired = required && isRequiredLanguage(language);
      const hasError = Boolean(fieldErrors[language]);

      // 탭 라벨 생성 (국기 + 언어명 + 번역 상태 표시기)
      const tabLabel = (
        <div className="i18n-tab-label">
          <span className="i18n-tab-flag">{languageInfo.flag}</span>
          <span className="i18n-tab-name">{languageInfo.label}</span>
          {isRequired && <span className="i18n-required-indicator">*</span>}
          {hasTranslation && (
            <Badge
              dot
              className="i18n-translation-indicator"
              style={{ backgroundColor: hasError ? '#ff4d4f' : '#52c41a' }}
            />
          )}
        </div>
      );

      // 입력 필드 생성
      const fieldValue = value[language] || '';
      const fieldPlaceholder =
        placeholder[language] || `${languageInfo.label} - Enter ${label || fieldName}`;

      const InputComponent = multiline ? TextArea : Input;
      const inputProps = multiline
        ? {
            rows,
            autoSize: { minRows: rows, maxRows: rows + 3 },
          }
        : {};

      const inputContent = (
        <div className="i18n-input-container">
          <InputComponent
            value={fieldValue}
            placeholder={fieldPlaceholder}
            disabled={disabled}
            onChange={e => handleFieldChange(language, e.target.value)}
            onBlur={e => handleFieldBlur(language, e.target.value)}
            className={`i18n-input ${isRequired ? 'i18n-required' : ''}`}
            status={hasError ? 'error' : undefined}
            maxLength={fieldConfig.maxLength}
            showCount={multiline}
            {...inputProps}
          />
          {fieldErrors[language] && (
            <div className="i18n-field-error">
              <Text type="danger" className="i18n-error-text">
                {fieldErrors[language]}
              </Text>
            </div>
          )}
        </div>
      );

      return {
        key: language,
        label: tabLabel,
        children: inputContent,
      };
    });
  }, [
    supportedLanguages,
    translationStatus,
    fieldErrors,
    required,
    value,
    placeholder,
    label,
    fieldName,
    disabled,
    multiline,
    rows,
    fieldConfig.maxLength,
    handleFieldChange,
    handleFieldBlur,
  ]);

  // 탭 변경 핸들러
  const handleTabChange = useCallback((key: string) => {
    setActiveTab(key as LanguageCode);
  }, []);

  // 전체 검증 상태 계산
  const _hasAnyTranslation = useMemo(() => {
    // Task 8에서 사용 예정
    return supportedLanguages.some(lang => translationStatus[lang]);
  }, [supportedLanguages, translationStatus]);

  const hasRequiredTranslation = useMemo(() => {
    if (!required) return true;
    return I18N_CONFIG.requiredLanguages.some(lang => translationStatus[lang]);
  }, [required, translationStatus]);

  return (
    <div className="i18n-input-wrapper">
      {/* 라벨 및 도움말 */}
      {(label || helperText) && (
        <div className="i18n-input-header">
          {label && (
            <Text strong className="i18n-input-label">
              {label}
              {required && <span className="i18n-required-indicator"> *</span>}
            </Text>
          )}
          {helperText && (
            <Text type="secondary" className="i18n-helper-text">
              {helperText}
            </Text>
          )}
        </div>
      )}

      {/* 전체 검증 상태 표시 */}
      {required && (
        <div
          className={`i18n-validation-summary ${hasRequiredTranslation ? 'success' : 'warning'}`}
        >
          <Text
            type={hasRequiredTranslation ? 'success' : 'warning'}
            className="i18n-validation-status"
          >
            {hasRequiredTranslation ? '✓ Required language condition met' : '⚠ Required language input needed'}
          </Text>
        </div>
      )}

      {/* 탭 기반 입력 인터페이스 */}
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={tabItems}
        className={`i18n-tabs ${error ? 'i18n-tabs-error' : ''}`}
        size="small"
        tabPosition="top"
      />
    </div>
  );
};

export default I18nInput;
